/**
 * Egen cookie-samtyckeshantering kopplad till Google Consent Mode v2.
 *
 * - Ett litet skript i <head> (CONSENT_BOOTSTRAP) sätter alla icke nödvändiga
 *   kategorier till "denied" innan GTM laddas, och läser in ett tidigare val
 *   ur cookien så att återkommande besökare aldrig ser bannern blinka till.
 * - Bannern (ConsentBanner) sparar valet i cookien, uppdaterar Consent Mode
 *   och skickar händelsen `consent_update` till dataLayer för GTM.
 *
 * Taggar i GTM styrs av Consent Mode (inbyggda samtyckeskontroller) och kan
 * dessutom triggas på händelsen `consent_update`.
 */

export const CONSENT_COOKIE = "em_consent";
/** Höj versionen om kategorierna ändras – då frågar vi igen. */
export const CONSENT_VERSION = 1;
/** Hur länge valet sparas (12 månader, IMY:s rekommendation om att fråga om). */
export const CONSENT_MAX_AGE_DAYS = 365;

export interface ConsentChoice {
  /** Funktionella cookies (t.ex. inbäddade tjänster, chatt). */
  functional: boolean;
  /** Statistik/analys (GA4 m.fl.). */
  analytics: boolean;
  /** Marknadsföring (Google Ads, Meta m.fl.). */
  marketing: boolean;
}

export interface StoredConsent extends ConsentChoice {
  v: number;
  /** Tidpunkt för valet, ISO-sträng. */
  at: string;
}

export const ALL_GRANTED: ConsentChoice = { functional: true, analytics: true, marketing: true };
export const ONLY_NECESSARY: ConsentChoice = { functional: false, analytics: false, marketing: false };

type ConsentState = "granted" | "denied";

/** Google Consent Mode v2-signaler för ett val. */
export function toConsentMode(choice: ConsentChoice): Record<string, ConsentState> {
  const g = (on: boolean): ConsentState => (on ? "granted" : "denied");
  return {
    ad_storage: g(choice.marketing),
    ad_user_data: g(choice.marketing),
    ad_personalization: g(choice.marketing),
    analytics_storage: g(choice.analytics),
    functionality_storage: g(choice.functional),
    personalization_storage: g(choice.functional),
    security_storage: "granted",
  };
}

export function parseConsent(raw: string | null | undefined): StoredConsent | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(decodeURIComponent(raw)) as Partial<StoredConsent>;
    if (value.v !== CONSENT_VERSION) return null;
    return {
      v: CONSENT_VERSION,
      at: typeof value.at === "string" ? value.at : "",
      functional: value.functional === true,
      analytics: value.analytics === true,
      marketing: value.marketing === true,
    };
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return match ? match.slice(name.length + 1) : null;
}

export function readConsent(): StoredConsent | null {
  return parseConsent(readCookie(CONSENT_COOKIE));
}

type GtagWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void };

/** Sparar valet, uppdaterar Consent Mode och meddelar GTM. */
export function saveConsent(choice: ConsentChoice): StoredConsent {
  const stored: StoredConsent = { v: CONSENT_VERSION, at: new Date().toISOString(), ...choice };
  if (typeof document === "undefined") return stored;
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(stored))}; Max-Age=${
    CONSENT_MAX_AGE_DAYS * 86400
  }; Path=/; SameSite=Lax${secure}`;
  document.documentElement.dataset["consent"] = "set";

  const w = window as GtagWindow;
  w.dataLayer = w.dataLayer ?? [];
  const gtag =
    w.gtag ??
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
  gtag("consent", "update", toConsentMode(choice));
  const flags = {
    consent_functional: choice.functional,
    consent_analytics: choice.analytics,
    consent_marketing: choice.marketing,
  };
  w.dataLayer.push({ event: "consent_update", ...flags });
  // Samma händelsenamn som Cookiebot skickade – befintliga GTM-triggers
  // ("C.E - cookie_consent_update") fortsätter då att fungera.
  w.dataLayer.push({ event: "cookie_consent_update", ...flags });
  return stored;
}

// --- Öppna inställningarna från t.ex. sidfoten -----------------------------

const openListeners = new Set<() => void>();

export function openConsentSettings() {
  openListeners.forEach((l) => l());
}

export function onOpenConsentSettings(listener: () => void) {
  openListeners.add(listener);
  return () => {
    openListeners.delete(listener);
  };
}

/**
 * Körs först i <head>, före GTM. Sätter standardläget "denied" och läser in ett
 * sparat val. `data-consent="set"` på <html> döljer bannern redan innan React
 * har laddats, så den aldrig blinkar till för den som redan har valt.
 * Skriptet körs bara en gång per sidladdning – head-skript kan köras om vid
 * klientnavigering, och då skulle händelserna annars skickas igen.
 */
export const CONSENT_BOOTSTRAP = `(function(w,d){if(w.__emConsentBoot)return;w.__emConsentBoot=1;w.dataLayer=w.dataLayer||[];function gtag(){w.dataLayer.push(arguments);}w.gtag=w.gtag||gtag;gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',functionality_storage:'denied',personalization_storage:'denied',security_storage:'granted',wait_for_update:500});gtag('set','ads_data_redaction',true);gtag('set','url_passthrough',true);try{var m=d.cookie.match(/(?:^|; )${CONSENT_COOKIE}=([^;]*)/);if(!m)return;var c=JSON.parse(decodeURIComponent(m[1]));if(c.v!==${CONSENT_VERSION})return;var g=function(b){return b===true?'granted':'denied';};gtag('consent','update',{ad_storage:g(c.marketing),ad_user_data:g(c.marketing),ad_personalization:g(c.marketing),analytics_storage:g(c.analytics),functionality_storage:g(c.functional),personalization_storage:g(c.functional)});var f={consent_functional:c.functional===true,consent_analytics:c.analytics===true,consent_marketing:c.marketing===true};w.dataLayer.push(Object.assign({event:'consent_restored'},f));w.dataLayer.push(Object.assign({event:'cookie_consent_update'},f));d.documentElement.setAttribute('data-consent','set');}catch(e){}})(window,document);`;
